import type { ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ErrorFallbackProps = {
  /** The whole app crashed: full-screen card with an h1. Otherwise an inline block with an h2. */
  fullScreen?: boolean;
  title: string;
  description: string;
  /** Development-only details (error message and stack). */
  details?: { heading: string; message: string; trace?: string | null; traceLabel: string };
  actions: ReactNode;
  footer?: ReactNode;
};

/** What the error boundaries show instead of a crashed tree (theme tokens, works in dark mode). */
export function ErrorFallback({
  fullScreen = false,
  title,
  description,
  details,
  actions,
  footer,
}: ErrorFallbackProps) {
  const Heading = fullScreen ? 'h1' : 'h2';
  return (
    <div
      className={cn(
        'flex items-center justify-center',
        fullScreen ? 'min-h-screen bg-background p-4' : 'min-h-[400px] p-8'
      )}
    >
      <div
        role="alert"
        className={cn(
          'w-full text-center',
          fullScreen ? 'max-w-2xl rounded-2xl border bg-card p-8 shadow-sm' : 'max-w-lg'
        )}
      >
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <TriangleAlert className="h-8 w-8" aria-hidden />
          </div>
        </div>
        <Heading className="mb-3 text-2xl font-bold text-foreground">{title}</Heading>
        <p className="mb-6 text-muted-foreground">{description}</p>
        {details && (
          <div className="mb-6 rounded-xl bg-muted p-4 text-left">
            <p className="mb-2 text-sm font-semibold text-foreground">{details.heading}</p>
            <p className="font-mono text-sm text-destructive">{details.message}</p>
            {details.trace && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  {details.traceLabel}
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
                  {details.trace}
                </pre>
              </details>
            )}
          </div>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">{actions}</div>
        {footer}
      </div>
    </div>
  );
}
